import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ImageDTO, ImageQuota, GalleryImageDTO } from "../../shared/schemas";
import { apiFetch, apiUpload } from "./client";

const IMAGES_KEY = ["images"] as const;
const IMAGE_QUOTA_KEY = ["image-quota"] as const;
const ALL_IMAGES_KEY = ["images", "all"] as const;

export function useImagesQuery(entityType: "task" | "event", entityId: string) {
  return useQuery<ImageDTO[]>({
    queryKey: [...IMAGES_KEY, entityType, entityId],
    queryFn: () => apiFetch<ImageDTO[]>(`/images/entity/${entityType}/${entityId}`),
    enabled: !!entityId,
  });
}

export function useImageQuotaQuery() {
  return useQuery<ImageQuota>({
    queryKey: IMAGE_QUOTA_KEY,
    queryFn: () => apiFetch<ImageQuota>("/images/quota"),
  });
}

export function useUploadImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      file: File;
      taskId?: string;
      eventId?: string;
    }) => {
      const formData = new FormData();
      formData.append("file", params.file);
      if (params.taskId) formData.append("taskId", params.taskId);
      if (params.eventId) formData.append("eventId", params.eventId);
      return apiUpload<ImageDTO>("/images", formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IMAGES_KEY });
      queryClient.invalidateQueries({ queryKey: IMAGE_QUOTA_KEY });
      queryClient.invalidateQueries({ queryKey: ALL_IMAGES_KEY });
    },
  });
}

export function useAllImagesQuery() {
  return useQuery<GalleryImageDTO[]>({
    queryKey: ALL_IMAGES_KEY,
    queryFn: () => apiFetch<GalleryImageDTO[]>("/images/all"),
  });
}

export function useRenameImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, originalName }: { id: string; originalName: string }) =>
      apiFetch<ImageDTO>(`/images/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ originalName }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IMAGES_KEY });
      queryClient.invalidateQueries({ queryKey: ALL_IMAGES_KEY });
    },
  });
}

export function useDeleteImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/images/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IMAGES_KEY });
      queryClient.invalidateQueries({ queryKey: IMAGE_QUOTA_KEY });
      queryClient.invalidateQueries({ queryKey: ALL_IMAGES_KEY });
    },
  });
}
